import { useSocketContext } from "context/SocketContext";
import React, { useState } from "react";
import { FileData, useAuthUserColorSettings, useGameContext } from "../../context/GameContext";
import { getFileMeta, isAudio, isPdfFile, isVideo } from "../helpers/fileBrowserHelpers";
import { ImageViewer } from "./ImageViewer";
import { PDFViewer } from "./PDFViewer";
import { WindowTile } from "../../desktopWindow/desktopWindowTypes";
import { AudioViewer } from "./AudioViewer";
import { VideoViewer } from "./VideoViewer";
import { isMobile, isDesktop } from "react-device-detect";

interface Props {
	file: FileData;
	size: WindowTile["size"];
	name: string | undefined;
	handleFileForMobile?: () => void;
	openFromApp?: boolean; // it is possible to open a file from the filebrowser app or from the desktop menu.
	// this props only relevant for the mobile version, is here to indicate from where the file is being opened
	// as different behaviour are expected.
}

function useUserStrokeColor() {
	return useAuthUserColorSettings() || "#df4b26";
}

const FileViewer: React.FC<Props> = ({ name, handleFileForMobile, openFromApp, ...props }) => {
	const { room } = useGameContext();
	const { socket } = useSocketContext();
	const strokeColor = useUserStrokeColor();
	const [isFileReady, setIsFileReady] = useState(false);
	const { file, size } = props;
	const [fileSize, setFileSize] = useState<{ width: number; height: number }>({ width: 600, height: 600 });
	React.useEffect(() => {
		getFileMeta(file, !isPdfFile(file.fileUrl!))
			.then(({ width, height }) => {
				if (isMobile) {
					const topBarHeight = 41;
					const taskBarHeight = 70;
					const windowHeadersHeight = 102;
					setFileSize({ width, height: height - topBarHeight - taskBarHeight - windowHeadersHeight });
				} else if (isDesktop) setFileSize({ width, height });
			})
			.finally(() => {
				setIsFileReady(true);
			});
	}, [file]);

	if (!isFileReady || !room || !socket) {
		return null;
	}

	return (
		<>
			{isPdfFile(file.fileUrl!) ? (
				<PDFViewer
					file={file}
					{...size}
					handleFileForMobile={handleFileForMobile}
					name={name}
					openFromApp={openFromApp}
				/>
			) : isAudio(file.fileUrl!) ? (
				<AudioViewer
					height={fileSize.height}
					width={fileSize.width}
					file={file}
					handleFileForMobile={handleFileForMobile}
					name={name}
					openFromApp={openFromApp}
				/>
			) : isVideo(file.fileUrl!) ? (
				<VideoViewer
					height={fileSize.height}
					file={file}
					width={fileSize.width}
					handleFileForMobile={handleFileForMobile}
					name={name}
					openFromApp={openFromApp}
				/>
			) : (
				<ImageViewer
					fileId={file.id}
					imageSrc={file.fileUrl!}
					height={fileSize.height}
					width={fileSize.width}
					roomId={room.roomId}
					socket={socket}
					strokeColor={strokeColor}
					handleFileForMobile={handleFileForMobile}
					name={name}
					openFromApp={openFromApp}
				/>
			)}
		</>
	);
};

export default React.memo(FileViewer);
